import {
  FingerprintGenerator,
  KeyHelper,
  SessionBuilder,
  SessionCipher,
  SignalProtocolAddress,
  type DeviceType,
} from '@privacyresearch/libsignal-protocol-typescript';
import { IndexedDbSignalStore } from './store.js';
import {
  base64ToBinaryString,
  base64ToBuffer,
  binaryStringToBase64,
  bufferToBase64,
  bufferToText,
  textToBuffer,
} from './encoding.js';

/**
 * Signal-Protokoll im Browser: X3DH-Sitzungsaufbau und Double Ratchet.
 *
 * ACHTUNG — Stand der Implementierung:
 * Hier läuft `@privacyresearch/libsignal-protocol-typescript`, ein
 * Community-Port. Das ist NICHT das von Signal auditierte `libsignal`, das als
 * Node-Native-Modul nicht im Browser läuft (docs/01-architektur.md §4.2).
 *
 * Solange dieser Port aktiv ist, darf das Produkt nicht als "Signal-Protokoll,
 * auditiert" beworben werden. Das ist eine Produktaussage, keine technische
 * Kleinigkeit. Der Wechsel auf einen eigenen WASM-Build von libsignal ist eine
 * Änderung an dieser Datei und an store.ts — nicht an der übrigen Anwendung.
 *
 * Nicht enthalten, weil bewusst nicht im MVP: Gruppen (Sender Keys),
 * Sealed Sender, Post-Quantum (PQXDH).
 */

const store = new IndexedDbSignalStore();

/** Jedes Gerät ist eine eigene Identität; deviceId ist hier die Adresse. */
function addressOf(deviceId: string): SignalProtocolAddress {
  return new SignalProtocolAddress(deviceId, 1);
}

export type GeneratedDeviceKeys = {
  identityKey: string;
  registrationId: number;
  signedPreKey: { keyId: number; publicKey: string; signature: string };
  oneTimePreKeys: Array<{ keyId: number; publicKey: string }>;
};

/**
 * Einmalig beim Einrichten des Geräts. Erzeugt alles Schlüsselmaterial LOKAL;
 * nur die öffentlichen Teile werden zurückgegeben und an den Server geschickt.
 * Private Schlüssel verlassen den Browser nicht.
 */
export async function generateDeviceKeys(): Promise<GeneratedDeviceKeys> {
  const identityKeyPair = await KeyHelper.generateIdentityKeyPair();
  const registrationId = KeyHelper.generateRegistrationId();

  await store.setIdentity(identityKeyPair, registrationId);

  const signedPreKeyId = 1;
  const signedPreKey = await KeyHelper.generateSignedPreKey(identityKeyPair, signedPreKeyId);
  await store.storeSignedPreKey(signedPreKeyId, signedPreKey.keyPair);

  // 100 Stück: Jeder wird vom Server genau einmal herausgegeben und trägt die
  // Forward Secrecy der jeweils ersten Nachricht einer neuen Sitzung.
  const oneTimePreKeys: Array<{ keyId: number; publicKey: string }> = [];
  for (let keyId = 1; keyId <= 100; keyId++) {
    const preKey = await KeyHelper.generatePreKey(keyId);
    await store.storePreKey(keyId, preKey.keyPair);
    oneTimePreKeys.push({ keyId, publicKey: bufferToBase64(preKey.keyPair.pubKey) });
  }

  return {
    identityKey: bufferToBase64(identityKeyPair.pubKey),
    registrationId,
    signedPreKey: {
      keyId: signedPreKeyId,
      publicKey: bufferToBase64(signedPreKey.keyPair.pubKey),
      signature: bufferToBase64(signedPreKey.signature),
    },
    oneTimePreKeys,
  };
}


export type PreKeyBundleResponse = {
  deviceId: string;
  registrationId: number;
  identityKey: string;
  signedPreKey: { keyId: number; publicKey: string; signature: string };
  oneTimePreKey: { keyId: number; publicKey: string } | null;
};

/**
 * X3DH: Sitzung zu einer Gegenstelle aufbauen.
 *
 * `processPreKey` prüft dabei die Signatur des Signed Prekey gegen den
 * Identity Key des Gegenübers. Ein Server, der ein gefälschtes Bundle
 * ausliefert, scheitert hier — er müsste den privaten Identity Key besitzen.
 */
/**
 * Besteht mit diesem Gerät schon eine Signal-Sitzung?
 *
 * Wichtig VOR dem Abruf eines Prekey-Bundles: Der Server löscht den
 * One-Time-Prekey beim Herausgeben (routes/keys.ts). Wer bei jeder Nachricht
 * ein Bundle holt, verbrennt also bei jeder Nachricht einen Prekey der
 * Gegenstelle — auch wenn er ihn gar nicht braucht, weil die Sitzung längst
 * steht. Bei 100 Prekeys ist der Vorrat nach ~100 Nachrichten leer, und
 * danach läuft X3DH ohne One-Time-Prekey, also mit schwächerer Forward
 * Secrecy für die erste Nachricht.
 */
export async function hasSessionWith(deviceId: string): Promise<boolean> {
  return store.hasSession(addressOf(deviceId).toString());
}

export async function ensureSession(bundle: PreKeyBundleResponse): Promise<void> {
  const address = addressOf(bundle.deviceId);
  if (await store.hasSession(address.toString())) return;

  const device: DeviceType = {
    identityKey: base64ToBuffer(bundle.identityKey),
    signedPreKey: {
      keyId: bundle.signedPreKey.keyId,
      publicKey: base64ToBuffer(bundle.signedPreKey.publicKey),
      signature: base64ToBuffer(bundle.signedPreKey.signature),
    },
    registrationId: bundle.registrationId,
    ...(bundle.oneTimePreKey
      ? {
          preKey: {
            keyId: bundle.oneTimePreKey.keyId,
            publicKey: base64ToBuffer(bundle.oneTimePreKey.publicKey),
          },
        }
      : {}),
  };

  await new SessionBuilder(store, address).processPreKey(device);
}

/**
 * Double-Ratchet-Verschlüsselung. Jede Nachricht nutzt einen neuen
 * Schlüssel — ein kompromittierter Schlüssel gibt weder frühere noch spätere
 * Nachrichten preis.
 */
export async function encryptMessage(
  peerDeviceId: string,
  text: string,
): Promise<{ ciphertext: string; type: 'prekey' | 'ratchet' }> {
  const cipher = new SessionCipher(store, addressOf(peerDeviceId));
  const message = await cipher.encrypt(textToBuffer(text));

  return {
    ciphertext: binaryStringToBase64(message.body ?? ''),
    // Typ 3 ist die X3DH-Initialnachricht, Typ 1 eine laufende Sitzung.
    type: message.type === 3 ? 'prekey' : 'ratchet',
  };
}

export async function decryptMessage(
  peerDeviceId: string,
  type: string,
  ciphertextBase64: string,
): Promise<string> {
  const cipher = new SessionCipher(store, addressOf(peerDeviceId));
  const body = base64ToBinaryString(ciphertextBase64);

  const plaintext =
    type === 'prekey'
      ? await cipher.decryptPreKeyWhisperMessage(body, 'binary')
      : await cipher.decryptWhisperMessage(body, 'binary');

  return bufferToText(plaintext);
}

/**
 * Safety Number zum Vergleich zwischen zwei Personen.
 *
 * Der Mechanismus, der das gesamte Sicherheitsversprechen trägt: Ein Server,
 * der ein zusätzliches Gerät einschleust, kann mitlesen — vollständig
 * E2E-konform, ohne einen einzigen Krypto-Bruch. Sichtbar wird das nur, wenn
 * beide Seiten ihre Nummer vergleichen.
 *
 * Verwendet den FingerprintGenerator der Bibliothek mit 5200 Iterationen —
 * dieselbe Konstruktion wie bei Signal. Die Zahl selbst zu bauen wäre genau
 * der Eigenbau-Fehler, den dieses Projekt vermeiden will.
 */
export async function safetyNumber(
  ownDeviceId: string,
  peerDeviceId: string,
): Promise<string | null> {
  const own = await store.getIdentityKeyPair();
  const peer = await store.loadIdentityKey(addressOf(peerDeviceId).toString());
  if (!own || !peer) return null;

  return new FingerprintGenerator(5200).createFor(ownDeviceId, own.pubKey, peerDeviceId, peer);
}

/**
 * Einen geaenderten Schluessel bewusst annehmen.
 *
 * Erst nach ausdruecklicher Bestaetigung durch den Nutzer aufrufen. Danach
 * gilt der neue Schluessel als vertraut und die Sitzung kann neu aufgebaut
 * werden — die alte muss dafuer weichen, sonst rechnet der Double Ratchet mit
 * einem Schluessel weiter, den es nicht mehr gibt.
 */
export async function acceptIdentityChange(peerDeviceId: string): Promise<void> {
  const address = addressOf(peerDeviceId).toString();
  await store.removeIdentity(address);
  await store.removeSession(address);
}

/** True, wenn sich der Schlüssel des Gegenübers seit dem letzten Mal geändert hat. */
export async function hasIdentityChanged(
  peerDeviceId: string,
  identityKeyBase64: string,
): Promise<boolean> {
  const known = await store.loadIdentityKey(addressOf(peerDeviceId).toString());
  if (!known) return false;
  return bufferToBase64(known) !== identityKeyBase64;
}
